import { Component } from '@theme/component';
import { bootTurnstile, turnstileRequired, turnstileToken as solveTurnstile } from '@theme/turnstile';

/**
 * "Notify me at launch" email capture.
 *
 * Posts to the App Proxy at /apps/invite/notify. No account, no application —
 * the Worker just stores the email. Progressive enhancement: a real <form> that
 * posts without JS; this only upgrades it to stay in place.
 */
class NotifyForm extends Component {
  /** @type {boolean} */
  #busy = false;

  mount() {
    this.on(this, 'submit', (event) => {
      event.preventDefault();
      this.#submit();
    });

    // THE ONLY LAZY TURNSTILE IN THE THEME, and only for the footer instance:
    // sections/footer.liquid renders the widget with `lazy: true`, so nothing
    // has fetched Cloudflare's 26.5KB script yet. Boot it the moment the
    // visitor touches this form, which is many seconds before they can have
    // typed an email and pressed send. bootTurnstile is idempotent and a no-op
    // for the eager widget in blocks/notify-form.liquid, so both instances of
    // this component run the same mount. `once` is only an optimisation here;
    // the guard lives in the module.
    const boot = () => bootTurnstile(this);
    this.on(this, 'focusin', boot, { once: true });
    this.on(this, 'pointerdown', boot, { once: true });
  }

  async #submit() {
    if (this.#busy) return;

    const email = this.#value('email');
    if (!email) {
      this.#status('Please enter your email address.', 'error');
      return;
    }

    this.#setBusy(true);
    const turnstileToken = await solveTurnstile(this, () =>
      this.#status('Verifying you are human…', 'pending')
    );
    if (turnstileRequired(this) && !turnstileToken) {
      this.#status('We could not verify your browser. Please refresh and try again.', 'error');
      this.#setBusy(false);
      return;
    }
    this.#status('Adding you…', 'pending');

    try {
      const response = await fetch('/apps/invite/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstileToken }),
      });

      if (response.status === 403) {
        this.#status('We could not verify your browser. Please refresh and try again.', 'error');
        return;
      }
      if (response.status === 400) {
        let message = 'Please enter a valid email address.';
        try { const b = await response.json(); if (b && b.message) message = b.message; } catch {}
        this.#status(message, 'error');
        return;
      }
      if (!response.ok) {
        this.#status('Something went wrong. Please try again shortly.', 'error');
        return;
      }

      this.#status('Thank you. We will be in touch.', 'success');
      const input = /** @type {HTMLInputElement | undefined} */ (this.refs['email']);
      if (input) input.value = '';
    } catch {
      this.#status('Could not reach the server. Please try again.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {string} ref @returns {string} */
  #value(ref) {
    const el = /** @type {HTMLInputElement | undefined} */ (this.refs[ref]);
    return el?.value?.trim() ?? '';
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs['submit']);
    if (button) button.disabled = busy;
  }

  /** @param {string} message @param {'pending' | 'error' | 'success'} tone */
  #status(message, tone) {
    const el = /** @type {HTMLElement | undefined} */ (this.refs['status']);
    if (!el) return;
    el.textContent = message;
    el.dataset.tone = tone;
  }
}

if (!customElements.get('notify-form')) {
  customElements.define('notify-form', NotifyForm);
}
